import React, { useContext, useEffect, useState } from 'react';

import { RouteComponentProps, useParams } from '@gatsbyjs/reach-router';
import { withStyles } from '@material-ui/core/styles';

import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import TabPanel from '../../components/tab-panel';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  a11yTabProps, checkUserAuthenticated, getAppRoutePagePaths, goToRoutePage, redirectRoutePage,
  setWindowTitle
} from '../../utils/helper';
import { AddIcon, MoreIcon, Program360Icon, RefreshIcon } from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, FormControl, Grid, Hidden, IconButton, Link, ListItemIcon,
  ListItemText, Menu, MenuItem, Select, Tab, Tabs, Tooltip, Typography
} from '../../utils/material-ui-core';
import ClientCompanyList from './clientcompany-list';
import DevicelocationList from './devicelocation-list';
import KitList from './kit-list';
import Overview from './overview';
import ProgramList from './program-list';
import SKUList from './sku-list';

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: theme.palette.secondary.main,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const Onboarding = (_: RouteComponentProps) => {
  const globalStyles = GlobalStyles();

  const { pathName } = useParams();

  const { writeLog, refreshData, onboardingDataRefreshInProgress, isUserAuthenticated, onboardingPagePathName, setOnboardingPagePathName, appRoutePaths, referenceLookups, userFunctionAccess } = useContext(AppContext);

  const [pathNameExists, setPathNameExists] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [onboardingPagePaths, setOnboardingPagePaths] = useState<AppPagePath[]>([] as AppPagePath[]);
  const [openNewSKUDialog, setOpenNewSKUDialog] = useState<boolean>(false);
  const [openNewDeviceLocationDialog, setOpenNewDeviceLocationDialog] = useState<boolean>(false);
  const [openSelectProgramDialog, setOpenSelectProgramDialog] = useState<boolean>(false);
  const [refreshSelectedProgram, setRefreshSelectedProgram] = useState<boolean>(false);
  const [sku, setSKU] = useState<string>("");
  const [gridMoreAnchorEl, setGridMoreAnchorEl] = React.useState(null);

  const handleGridMoreMenuClick = (event) => {
    setGridMoreAnchorEl(event.currentTarget);
  };

  const handleGridMoreMenuClose = () => {
    setGridMoreAnchorEl(null);
  };

  const handleOnboardingPagePathChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    writeLog("Onboarding Page", "Tab change to " + newValue.toLowerCase(), "", "");
    setOnboardingPagePathName(newValue);
  };

  const showSelectProgramDialog = () => {
    writeLog("Onboarding Page", "Opening program 360 search modal.", "", "");
    setOpenSelectProgramDialog(true);
    setTimeout(() => {
      setOpenSelectProgramDialog(false);
    }, 1000);
  };

  const showOnboardProgram = () => {
    if (onboardingPagePathName.toLowerCase() === "program 360") {
      return true;
    }
    return false;
  };

  const refresh = () => {
    if (onboardingPagePathName.toLowerCase() === "program 360") {
      setRefreshSelectedProgram(true);
      setTimeout(() => {
        setRefreshSelectedProgram(false);
      }, 1000);
    } else {
      refreshData(currentPathTitle());
    }
  };

  const showNewRecord = () => {
    if (onboardingPagePathName.toLowerCase() === "companies" && userFunctionAccess.onboardingCompanyAdminWrite) {
      return true;
    } else if (onboardingPagePathName.toLowerCase() === "programs" && userFunctionAccess.onboardingProgramAdminWrite) {
      return true;
    } else if (onboardingPagePathName.toLowerCase() === "kits" && userFunctionAccess.onboardingKitAdminWrite) {
      return true;
    } else if (onboardingPagePathName.toLowerCase() === "skus" && userFunctionAccess.skuAdminWrite) {
      return true;
    } else if (onboardingPagePathName.toLowerCase() === "device locations" && userFunctionAccess.deviceLocationWrite) {
      return true;
    }
    return false;
  };

  const newRecord = () => {
    if (onboardingPagePathName.toLowerCase() === "companies") {
      writeLog("Onboarding Page", "Opening new client company detail.", "", "");
      goToRoutePage("onboarding/clientcompanies/" + referenceLookups.newRecordDefaultId);
    } else if (onboardingPagePathName.toLowerCase() === "programs") {
      writeLog("Onboarding Page", "Opening new program detail.", "", "");
      goToRoutePage("onboarding/programs/" + referenceLookups.newRecordDefaultId);
    } else if (onboardingPagePathName.toLowerCase() === "skus") {
      writeLog("Onboarding Page", "Opening new sku detail.", "", "");
      setOpenNewSKUDialog(true);
      setTimeout(() => {
        setOpenNewSKUDialog(false);
      }, 1000);
    } else if (onboardingPagePathName.toLowerCase() === "kits") {
      writeLog("Onboarding Page", "Opening new kit detail.", "", "");
      goToRoutePage("onboarding/kits/" + referenceLookups.newRecordDefaultId);
    } else if (onboardingPagePathName.toLowerCase() === "device locations") {
      writeLog("Onboarding Page", "Opening new device location detail.", "", "");
      setOpenNewDeviceLocationDialog(true);
      setTimeout(() => {
        setOpenNewDeviceLocationDialog(false);
      }, 1000);
    }
  };

  const getParamPathTitle = (paths: AppPagePath[]) => {
    let pathTitle = "";
    if (pathName !== null && pathName !== undefined && pathName !== "") {
      paths.forEach((path) => {
        if (path.title !== undefined && path.title !== null) {
          if (path.title.toLowerCase().replaceAll(" ", "") === pathName) {
            pathTitle = path.title;
          }
        }
      });
    }
    return pathTitle;
  };

  const currentPathTitle = () => {
    let pathTitle: any = onboardingPagePaths.find((onboardingPagePath) => onboardingPagePath.title === onboardingPagePathName)?.title;
    if (pathTitle === null || pathTitle === undefined) {
      pathTitle = "";
    }
    return pathTitle;
  };

  const gotoSKU = (sku: string) => {
    setSKU(sku);
    setOnboardingPagePathName("SKUs");
    setTimeout(() => {
      gotoSKU2();
    }, 1000);
  };

  const gotoSKU2 = () => {
    setOpenNewSKUDialog(true);
    setTimeout(() => {
      setOpenNewSKUDialog(false);
      setSKU("");
    }, 3000);
  };

  useEffect(() => {
    writeLog("Onboarding Page", "Tab " + onboardingPagePathName.toLowerCase() + ".", "", "");
    setWindowTitle("Onboarding");
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initializePage = async () => {
      checkUserAuthenticated(isUserAuthenticated);
      if (isUserAuthenticated) {
        const tempOnboardingPagePaths: AppPagePath[] = getAppRoutePagePaths("Onboarding", appRoutePaths);
        const pathTitle = getParamPathTitle(tempOnboardingPagePaths);

        if (pathTitle !== "") {
          setOnboardingPagePathName(pathTitle);
          setPathNameExists(true);
        }

        setOnboardingPagePaths(tempOnboardingPagePaths);

        setTimeout(() => {
          setLoading(false);
        }, 1000);

      }
    };
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated]);

  return (
    <React.Fragment>
      {loading || onboardingDataRefreshInProgress ? (
        <SplashWait />
      ) : (
        <Screen>
          <Grid container>
            <Grid item xs={12}>
              <Grid container spacing={3} className={globalStyles.bottomSpacer}>
                <Grid item xs={11}>
                  <Box className={globalStyles.ellipsis}>
                    <Breadcrumbs>
                      <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                        Home {pathNameExists}
                      </Link>

                      {pathNameExists ? (
                        <Link color="inherit" onClick={() => redirectRoutePage("onboarding")} className={globalStyles.link}>
                          Onboarding
                        </Link>
                      ) : (
                        <Typography color="textPrimary">Onboarding</Typography>
                      )}

                      {pathNameExists ? (
                        <FormControl>
                          <Select labelId="demo-simple-select-label" id="demo-simple-select" value={onboardingPagePathName}>
                            {onboardingPagePaths.map((onboardingPagePath, index) => {
                              return (
                                <MenuItem
                                  value={onboardingPagePath.title}
                                  key={index}
                                  onClick={() => {
                                    setOnboardingPagePathName(onboardingPagePath.title);
                                  }}
                                >
                                  {onboardingPagePath.title}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography color="textPrimary"></Typography>
                      )}
                    </Breadcrumbs>
                  </Box>
                </Grid>

                <Grid item xs={1}>
                  <Hidden xsDown>
                    <Box display="flex" justifyContent="flex-end" width={1}>
                      {showOnboardProgram() ? (
                        <Box onClick={() => showSelectProgramDialog()}>
                          <IconButton>
                            <Tooltip arrow title={"Search Programs for 360 view"}>
                              <Program360Icon color="secondary" />
                            </Tooltip>
                          </IconButton>
                        </Box>
                      ) : null}

                      {showNewRecord() ? (
                        <Box onClick={() => newRecord()}>
                          <IconButton>
                            <Tooltip arrow title={"Add new " + currentPathTitle()}>
                              <AddIcon color="secondary" />
                            </Tooltip>
                          </IconButton>
                        </Box>
                      ) : null}
                      <Box>
                        <IconButton>
                          <Tooltip arrow title={"Refresh " + currentPathTitle()}>
                            <RefreshIcon color="secondary" onClick={() => refresh()} />
                          </Tooltip>
                        </IconButton>
                      </Box>
                    </Box>
                  </Hidden>

                  <Hidden smUp>
                    <Box display="flex" justifyContent="flex-end" width={1}>
                      <Tooltip arrow title="Menu">
                        <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleGridMoreMenuClick}>
                          <MoreIcon />
                        </Button>
                      </Tooltip>

                      <Menu elevation={2} id="grid-menu" anchorEl={gridMoreAnchorEl} keepMounted open={Boolean(gridMoreAnchorEl)} onClose={handleGridMoreMenuClose}>
                        {showNewRecord() ? (
                          <StyledMenuItem
                            onClick={(e) => {
                              newRecord();
                              handleGridMoreMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <AddIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={"New " + currentPathTitle()} />
                          </StyledMenuItem>
                        ) : null}

                        {showOnboardProgram() ? (
                          <StyledMenuItem
                            onClick={(e) => {
                              showSelectProgramDialog();
                              handleGridMoreMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <Program360Icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={"Program 360"} />
                          </StyledMenuItem>
                        ) : null}

                        <StyledMenuItem
                          onClick={(e) => {
                            refresh();
                            handleGridMoreMenuClose();
                          }}
                        >
                          <ListItemIcon>
                            <RefreshIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Refresh" />
                        </StyledMenuItem>
                      </Menu>
                    </Box>
                  </Hidden>
                </Grid>
              </Grid>

              {onboardingPagePaths.length > 0 ? (
                <React.Fragment>
                  {!pathNameExists ? (
                    <Tabs value={onboardingPagePathName} onChange={handleOnboardingPagePathChange} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" aria-label="Onboarding List">
                      {onboardingPagePaths.map((onboardingPagePath, index) => {
                        return (
                          <Tab
                            label={
                              <Tooltip arrow title={onboardingPagePath.note}>
                                <Box>{onboardingPagePath.title}</Box>
                              </Tooltip>
                            }
                            value={onboardingPagePath.title}
                            {...a11yTabProps(index)}
                            key={index}
                          />
                        );
                      })}

                      <Tab
                        label={
                          <Tooltip arrow title={""}>
                            <Box>Program 360</Box>
                          </Tooltip>
                        }
                        value={"Program 360"}
                        {...a11yTabProps(100)}
                      />
                    </Tabs>
                  ) : null}
                </React.Fragment>
              ) : (
                <React.Fragment></React.Fragment>
              )}

              <TabPanel id={"Program 360"} currentSelectedId={onboardingPagePathName} w="100%" h="100%">
                <Overview open={openSelectProgramDialog} refresh={refreshSelectedProgram} gotoSKUCallBack={gotoSKU} />
              </TabPanel>
              <TabPanel id={"Companies"} currentSelectedId={onboardingPagePathName} w="100%" h="70vh">
                <ClientCompanyList />
              </TabPanel>
              <TabPanel id={"Programs"} currentSelectedId={onboardingPagePathName} w="100%" h="70vh">
                <ProgramList />
              </TabPanel>
              <TabPanel id={"Kits"} currentSelectedId={onboardingPagePathName} w="100%" h="70vh">
                <KitList />
              </TabPanel>
              <TabPanel id={"SKUs"} currentSelectedId={onboardingPagePathName} w="100%" h="70vh">
                <SKUList showNew={openNewSKUDialog} sku={sku} />
              </TabPanel>
              <TabPanel id={"Device Locations"} currentSelectedId={onboardingPagePathName} w="100%" h="70vh">
                <DevicelocationList showNew={openNewDeviceLocationDialog} />
              </TabPanel>
            </Grid>
          </Grid>
        </Screen>
      )}
    </React.Fragment>
  );
};

export default Onboarding;
